import axios from 'axios';
import CONFIG from './config';
import { supabase } from '../config/supabase';

// Créer une instance Axios avec la configuration de base
const api = axios.create({
    baseURL: process.env.NODE_ENV === 'production' 
        ? 'https://fbatrackify.com'
        : 'http://localhost:4000',
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: true,
    timeout: CONFIG.DEFAULT_TIMEOUT,
    maxContentLength: 50 * 1024 * 1024, // 50MB
    maxBodyLength: 50 * 1024 * 1024, // 50MB
    maxRedirects: 5
});

// Log les détails de la configuration
console.log('🌐 API configurée avec URL de base:', api.defaults.baseURL);

// Intercepteur de requêtes
api.interceptors.request.use(
    async config => {
        // Récupérer le token d'authentification
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
            config.headers.Authorization = `Bearer ${session.access_token}`;
        }
        return config;
    },
    error => {
        console.error('❌ Erreur lors de la préparation de la requête:', error);
        return Promise.reject(error);
    }
);

// Intercepteur de réponses
api.interceptors.response.use(
    response => {
        // Traitement des réponses réussies
        if (!CONFIG.isProduction) {
            //console.log(`✅ Réponse API: ${response.status} ${response.config.url}`);
        }
        return response;
    },
    error => {
        // Traitement des erreurs
        if (error.response) {
            // La requête a été faite et le serveur a répondu avec un code d'état hors de 2xx
            console.error(`❌ Erreur API ${error.response.status}: ${error.response.config.url}`);
            if (!CONFIG.isProduction) {
                console.error('Données de la réponse:', error.response.data);
            }

            // Rediriger vers la page de connexion si le token est invalide
            if (error.response.status === 401 && !window.location.pathname.includes('/auth')) {
                window.location.href = '/auth';
            }
        } else if (error.request) {
            // La requête a été faite mais aucune réponse n'a été reçue
            console.error('⚠️ Aucune réponse du serveur:', error.request.url || error.config?.url);
            console.error('Vérifiez que le serveur API est démarré sur', api.defaults.baseURL);
        } else {
            // Quelque chose s'est passé lors de la création de la requête
            console.error('❌ Erreur de configuration de la requête:', error.message);
        }
        
        return Promise.reject(error);
    }
);

// Récupère le sellerId Amazon
api.getSellerId = async function() {
    const res = await api.get('/api/amazon/seller-id');
    return res.data.sellerId;
};

// Vérifie les restrictions d'un produit sur plusieurs marketplaces
api.getRestrictions = async function({ asin, marketplaceIds, conditionType = 'new_new' }) {
    const params = {
        asin,
        marketplaceIds: marketplaceIds.join(','),
        conditionType
    };
    const res = await api.get('/api/amazon/restrictions', { params });
    return res.data;
};

// Vérifie les restrictions de plusieurs produits sur plusieurs marketplaces (bulk)
api.getRestrictionsBulk = async function({ asins, marketplaceIds, conditionType = 'new_new' }) {
    const res = await api.post('/api/amazon/restrictions/bulk', {
        asins,
        marketplaceIds,
        conditionType
    });
    return res.data;
};

// Récupère l'historique de repricing pour un produit et une marketplace
api.getRepricingHistory = async function({ repricingRulesId }) {
    const res = await api.get('/api/repricing-rules/history', { params: { repricingRulesId } });
    return res.data;
};

// Poste l'historique de repricing pour plusieurs produits
api.postBulkRepricingHistory = async function(repricingRulesIds) {
    const res = await api.post('/api/repricing-rules/history/bulk', { repricingRulesIds });
    return res.data;
};

// Récupère les 5 derniers changements de prix (repricing history global)
api.getLatestRepricingHistory = async function() {
    const res = await api.get('/api/repricing-rules/history/latest');
    return res.data;
};

// Récupère les taux de change pour les devises étrangères
api.getExchangeRates = async () => {
  const rates = {};
  for (const currency of ['SEK', 'PLN', 'GBP']) {
    try {
      const response = await api.get(`/api/currency/rate?from=${currency}&to=EUR`);
      rates[currency] = response.data?.rate || null;
    } catch (e) {
      rates[currency] = null;
    }
  }
  return rates;
};

export default api;